/**
 * 动态 inject 实现 - 配合统一 Store Registry 使用
 *
 * 核心能力：
 * 1. 复用统一的运行时 Store 注册中心
 * 2. 支持单个 Mod 按需加载
 * 3. 支持批量预加载
 * 4. 保持业务侧 @inject('XxxMod') 不变
 */

import React, { Component } from 'react';
// 通过 webpack alias 'mobx-react-original' 拿原始 observer，避免走 'mobx-react$' alias 造成循环引用。
import { observer as mobxObserver } from 'mobx-react-original';
import {
  // 确保单个 Store 已经就绪（可能触发动态 import）
  ensureRuntimeStore,
  // 批量确保多个 Store 已经就绪
  ensureRuntimeStores,
  // 获取当前已加载的 Store 列表（主要给调试/暴露能力用）
  getLoadedStoreNames,
  // 按名称读取运行时 Store
  getRuntimeStore,
  // 判断运行时是否已经真正持有某个 Store
  hasRuntimeStore,
  // 判断某个 Store 是否被声明为“允许懒加载”
  isLazyStore
} from './runtimeStoreRegistry';
import Loading from 'widget/Loading/Loading';

// 对外暴露的动态 inject。
//
// 用法与 mobx-react 的 inject 保持一致：
//   @inject('LoginMod', 'PersonalMod')
//   @observer
//   class XxxView extends React.Component {}
//
// 区别是：
// 1. 先从运行时 Store 容器里找
// 2. 找不到但允许懒加载时，会自动触发 chunk 加载
// 3. 加载完成后，再把对应 Store 作为 props 注入给业务组件
export function dynamicInject(...storeNames) {
  // 返回一个高阶组件工厂，行为上与 inject('Xxx')(WrappedComponent) 一致。
  return function (WrappedComponent) {
    // ---- 链式 @inject 合并优化 ----
    // 当业务代码写了多个 @inject 装饰器：
    //   @inject('AppMod')
    //   @inject('LoginMod')
    //   @inject('ShoppingCartMod')
    //   @observer
    //   class XxxView extends Component {}
    //
    // 装饰器从下往上依次执行，每次 inject 都会包一层 DynamicInjectWrapper。
    // 如果不做合并，N 个 @inject 会产生 N 层嵌套的 class 组件，
    // 每层各自有 state、componentDidMount（异步加载）、@mobxObserver —— 带来不必要的性能开销。
    //
    // 优化方式：
    // 检测 WrappedComponent 是否已经是上一轮 dynamicInject 产出的包装组件，
    // 如果是，就把本轮的 storeNames 合并进去，复用同一个真正的业务组件引用，
    // 最终只产生 **一个** DynamicInjectWrapper 实例。
    //
    // 注意：React.forwardRef 返回的组件需要特殊处理，标记属性在 render 属性上
    let actualComponent = WrappedComponent;
    let mergedStoreNames = storeNames;

    // 检查是否是 forwardRef 包装的组件（render 属性上有标记）
    const wrappedInfo = WrappedComponent.render || WrappedComponent;

    if (wrappedInfo.__dynamicInjectWrappedComponent) {
      // 合并 storeNames 并去重，保持声明顺序（当前 inject 在外层，排在前面）
      const seen = new Set();
      mergedStoreNames = [...storeNames, ...wrappedInfo.__dynamicInjectStoreNames].filter(
        (name) => {
          if (seen.has(name)) return false;
          seen.add(name);
          return true;
        }
      );
      // 穿透到最底层的真实业务组件
      actualComponent = wrappedInfo.__dynamicInjectWrappedComponent;
    }

    // 包装层本身也需要 observer，
    // 这样被注入的 Store 内部 observable 变化时，业务组件仍然能正常响应更新。
    @mobxObserver
    class DynamicInjectWrapper extends Component {
      state = {
        // 包装层先处于加载态，等确认所有 Store 都可用后再渲染真正业务组件。
        loading: true,
        // 记录加载异常，渲染兜底错误 UI，避免页面直接白屏。
        error: null
      };

      async componentDidMount() {
        try {
          // 第一步：筛出"既不在运行时容器里，也不在懒加载白名单里"的 Store。
          // 这种情况属于配置错误，例如：
          // - 首屏加载必要的mod 没注册
          // - lazyStoreImports / ModuleMap 没生成
          // - 业务侧 inject 名字写错了
          const missingStores = mergedStoreNames.filter(
            (storeName) => !hasRuntimeStore(storeName) && !isLazyStore(storeName)
          );

          if (missingStores.length > 0) {
            // 参考 mobx-react 的行为：不存在的 store 注入 undefined，只打印警告不报错
            console.warn(
              `⚠️ [DynamicInject] 以下 Store 未注册，将注入 undefined:`,
              missingStores,
              '\n请检查：',
              '\n1. Store 名称是否拼写正确',
              '\n2. 是否已在 StoreRegistry.js 中注册（首屏加载必要的mod 或 lazyStoreImports）'
            );
          }

          // 第二步：筛出"还没加载，但允许懒加载"的 Store。
          // 这些 Store 会在当前组件挂载后异步补齐。
          const needDynamicLoad = mergedStoreNames.filter(
            (storeName) => !hasRuntimeStore(storeName) && isLazyStore(storeName)
          );

          if (needDynamicLoad.length > 0) {
            // 这里打印日志主要是为了调试观察哪些页面首次进入时触发了动态 Mod 加载。
            console.log(`🔄 [DynamicInject] 组件需要加载:`, needDynamicLoad);
            // 等待所有缺失 Store 加载完成。
            // runtimeStoreRegistry 内部已经做了并发去重：
            // 同一个 Store 多个页面同时请求，也只会真正加载一次。
            await ensureRuntimeStores(needDynamicLoad);
          }

          // 第三步：再次校验一次。
          // 理论上经过 ensureRuntimeStores 后，这里应该都能拿到；
          // 如果仍拿不到，说明模块导出异常、加载失败或注册流程出了问题。
          const unresolvedStores = mergedStoreNames.filter(
            (storeName) => !hasRuntimeStore(storeName)
          );
          if (unresolvedStores.length > 0) {
            // 懒加载失败时仍然允许组件渲染，但打印错误日志便于排查
            console.error(
              `❌ [DynamicInject] 以下 Store 加载失败，将注入 undefined:`,
              unresolvedStores,
              '\n可能原因：',
              '\n1. ModuleMap 中的路径配置错误',
              '\n2. Store 文件导出格式不正确（需要 export default）',
              '\n3. Store 构造函数执行出错'
            );
          }

          // 所有依赖 Store 都准备好了，允许渲染业务组件。
          this.setState({ loading: false });
        } catch (error) {
          // 包装层统一兜底，避免动态加载失败时业务页面直接抛异常白屏。
          console.error('[DynamicInject] 加载失败:', error);
          this.setState({ loading: false, error });
        }
      }

      render() {
        const { loading, error } = this.state;

        // 还在等 Store 加载时，先渲染一个轻量占位。
        // 这里没有复用全局 Loading 组件，是为了保持包装层依赖足够轻，避免再引入额外体积或循环依赖。
        if (loading) {
          return (
            // <div
            //   style={{
            //     padding: '20px',
            //     textAlign: 'center',
            //     color: '#999'
            //   }}>
            //   加载中...
            // </div>
            <Loading />
          );
        }

        // 加载失败时给出明确兜底，避免页面完全空白。
        if (error) {
          return (
            <div
              style={{
                padding: '20px',
                textAlign: 'center',
                color: '#f00'
              }}>
              加载失败，请刷新重试
            </div>
          );
        }

        // 真正渲染业务组件前，从运行时容器里把目标 Store 逐个取出并拼成注入 props。
        // 这一步刻意按名称注入，保持与传统 mobx-react inject('XxxMod') 的调用习惯一致。
        // 参考 mobx-react 行为：不存在的 store 注入 undefined（而非不注入）
        const injectedProps = {};
        mergedStoreNames.forEach((storeName) => {
          injectedProps[storeName] = getRuntimeStore(storeName) || undefined;
        });

        // 剥离内部私有 prop forwardedRef，避免它被透传进业务组件污染其 props；
        // 其余原始 props 保留，再附加运行时注入的 Store。
        // 说明：这里一次解构即可，V8 对 rest-spread 已高度优化，无需再按有无 ref 分支，
        // 分支反而会导致 forwardedRef=null 时把该私有 prop 泄漏给业务组件。
        const { forwardedRef, ...restProps } = this.props;
        const ActualComponent = actualComponent;

        // forwardedRef 为 null（未使用 ref）时，ref={null} 对 React 是无副作用的合法写法
        return <ActualComponent {...restProps} {...injectedProps} ref={forwardedRef} />;
      }
    }

    // 补一个易读的 displayName，方便 React DevTools / 报错栈定位。
    const wrappedComponentName = actualComponent.displayName || actualComponent.name || 'Component';
    DynamicInjectWrapper.displayName = `DynamicInject(${wrappedComponentName})`;

    // 使用 React.forwardRef 包装，支持 ref 转发
    // 这样外部使用 <Component ref={...} /> 时，ref 能正确传递到内部真实组件
    const ForwardedComponent = React.forwardRef((props, ref) => {
      return <DynamicInjectWrapper {...props} forwardedRef={ref} />;
    });

    ForwardedComponent.displayName = `DynamicInject(${wrappedComponentName})`;

    // 供外层 @inject 识别并合并，避免多层嵌套。
    // 合并检测用的是 `WrappedComponent.render || WrappedComponent`：
    // - 正常情况下 forwardRef 对象暴露 .render（我们的 render 函数），标记打在它上面即可命中；
    // - 极端兜底：某些环境若拿不到 .render，则回退读组件对象本身，故这里两处都打标记。
    ForwardedComponent.render.__dynamicInjectWrappedComponent = actualComponent;
    ForwardedComponent.render.__dynamicInjectStoreNames = mergedStoreNames;
    ForwardedComponent.__dynamicInjectWrappedComponent = actualComponent;
    ForwardedComponent.__dynamicInjectStoreNames = mergedStoreNames;

    // 保存原始组件引用，兼容 mobx-react 的 wrappedComponent 约定
    ForwardedComponent.wrappedComponent = actualComponent;

    return ForwardedComponent;
  };
}

// 主动预加载一批 Mod。
// 常用于：
// 1. 首页空闲时预热高频二级 Store
// 2. App 入口空闲时预热常用业务 Mod
//
// 注意这里是“尽力而为”的预热：
// - 不阻塞页面渲染
// - 失败只打 warning，不向外抛错
export function preloadMods(modNames) {
  ensureRuntimeStores(modNames).catch((err) => {
    console.warn('[DynamicInject] 预加载失败:', err);
  });
}

// 带调度能力的预加载：
// 把 preloadMods 放到指定延时后的浏览器空闲时、或 window load 之后再执行，
// 避免和首屏关键渲染抢资源。
export function schedulePreloadMods(modNames, options = {}) {
  // SSR 环境、非法入参、空列表时直接返回空取消函数，调用方不需要再额外判空。
  if (typeof window === 'undefined' || !Array.isArray(modNames) || !modNames.length) {
    return () => {};
  }

  // idleTimeout:
  //   requestIdleCallback 的超时兜底；浏览器长时间不空闲时，到超时仍会尽快执行。
  // fallbackDelay:
  //   不支持 requestIdleCallback 时，退化为 setTimeout 延迟执行。
  // initialDelay:
  //   进入空闲调度前额外等待的时间，用于更低优先级的资源预热。
  // waitForWindowLoad:
  //   是否要等整个 window load 之后再开始调度，适合更保守的预热场景。
  const {
    idleTimeout = 3000,
    fallbackDelay = 2000,
    initialDelay = 0,
    waitForWindowLoad = false
  } = options;

  // 保存调度句柄，便于外部取消。
  let timeoutId = null;
  let delayTimeoutId = null;
  let idleCallbackId = null;
  let loadHandler = null;
  // 取消标记，避免组件卸载后仍继续触发预加载。
  let cancelled = false;

  // 真正执行预加载调度的核心函数。
  const run = () => {
    if (cancelled) {
      return;
    }

    // 优先使用浏览器空闲回调，把预热放到对用户最不敏感的时机。
    if (window.requestIdleCallback) {
      idleCallbackId = window.requestIdleCallback(
        () => {
          if (!cancelled) {
            preloadMods(modNames);
          }
        },
        { timeout: idleTimeout }
      );
    } else {
      // 旧浏览器 / 某些 WebView 不支持 requestIdleCallback，就退化成简单延时执行。
      timeoutId = window.setTimeout(() => {
        if (!cancelled) {
          preloadMods(modNames);
        }
      }, fallbackDelay);
    }
  };

  // 先执行可选的额外延时，再进入空闲调度。
  const scheduleRun = () => {
    if (initialDelay > 0) {
      delayTimeoutId = window.setTimeout(run, initialDelay);
    } else {
      run();
    }
  };

  // 某些场景希望更晚一点再预热，比如等首屏所有静态资源 load 完。
  if (waitForWindowLoad && document.readyState !== 'complete') {
    loadHandler = () => {
      window.removeEventListener('load', loadHandler);
      loadHandler = null;
      scheduleRun();
    };

    window.addEventListener('load', loadHandler);
  } else {
    // 默认直接进入空闲调度。
    scheduleRun();
  }

  // 返回取消函数，给 React 生命周期或外部手动清理使用。
  return () => {
    cancelled = true;

    if (timeoutId !== null) {
      window.clearTimeout(timeoutId);
    }

    if (delayTimeoutId !== null) {
      window.clearTimeout(delayTimeoutId);
    }

    if (idleCallbackId !== null && window.cancelIdleCallback) {
      window.cancelIdleCallback(idleCallbackId);
    }

    if (loadHandler) {
      window.removeEventListener('load', loadHandler);
    }
  };
}

// 对外暴露一个轻量判断：某个 Mod 是否已经被加载进运行时容器。
export function isModLoaded(modName) {
  return hasRuntimeStore(modName);
}

// 获取当前已加载 Mod 名单，主要给调试和埋点观察使用。
export function getLoadedMods() {
  return getLoadedStoreNames();
}

// 顺手透出单个 Store 的 ensure 能力，给少数需要手动控制加载时机的地方使用。
export { ensureRuntimeStore };

// 默认导出 dynamicInject，让 alias 后的 `import { inject } from 'mobx-react'`
// 可以无感替换成这里的实现。
export default dynamicInject;



// WEBPACK FOOTER //
// ./src/utils/storeRuntime/dynamicInject.js